import { useState } from "react";
import {
  Title,
  Text,
  Card,
  Group,
  Badge,
  Stack,
  TextInput,
  Button,
  Loader,
  Center,
} from "@mantine/core";
import { IconSearch, IconPlus, IconFileText } from "@tabler/icons-react";
import { useWorkspace } from "../contexts/WorkspaceContext";
import { usePrompts } from "../hooks/useApi";
import { promptService } from "../lib/services";
import {
  showSuccessNotification,
  showErrorNotification,
} from "../lib/notifications";
import { PromptForm } from "../components/PromptForm";
import type { CreatePromptBody } from "../lib/types";

export function PromptsPage() {
  const { activeWorkspace } = useWorkspace();
  const workspaceId = activeWorkspace?.id ?? null;
  const {
    data: prompts,
    loading,
    error,
    refetch,
  } = usePrompts(workspaceId);

  const [isCreating, setIsCreating] = useState(false);

  async function handleCreate(body: CreatePromptBody) {
    if (!workspaceId) return;
    await promptService.create(workspaceId, body);
    showSuccessNotification("Prompt created successfully");
    setIsCreating(false);
    refetch();
  }

  if (isCreating) {
    return (
      <PromptForm
        onSubmit={handleCreate}
        onCancel={() => setIsCreating(false)}
      />
    );
  }

  return (
    <>
      <Group justify="space-between" mb="md">
        <Title order={2}>Prompts</Title>
        <Button
          leftSection={<IconPlus size={16} />}
          size="sm"
          onClick={() => setIsCreating(true)}
        >
          New Prompt
        </Button>
      </Group>

      <TextInput
        placeholder="Search prompts..."
        leftSection={<IconSearch size={16} />}
        mb="lg"
      />

      {loading && (
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      )}

      {error && (
        <Text c="red" ta="center">
          {error}
        </Text>
      )}

      {!loading && !error && prompts && prompts.length === 0 && (
        <Center py="xl">
          <Stack align="center" gap="sm">
            <IconFileText size={48} color="var(--mantine-color-dimmed)" />
            <Text c="dimmed">No prompts yet</Text>
            <Button
              variant="light"
              leftSection={<IconPlus size={16} />}
              onClick={() => setIsCreating(true)}
            >
              Create your first prompt
            </Button>
          </Stack>
        </Center>
      )}

      {!loading && prompts && prompts.length > 0 && (
        <Stack>
          {prompts.map((prompt) => (
            <Card key={prompt.id} withBorder padding="md" radius="md">
              <Group justify="space-between">
                <div>
                  <Text fw={600}>{prompt.title}</Text>
                  {prompt.description && (
                    <Text size="sm" c="dimmed">
                      {prompt.description}
                    </Text>
                  )}
                </div>
                <Group gap="xs">
                  <Badge variant="light" color="blue" size="sm">
                    {prompt.slug}
                  </Badge>
                </Group>
              </Group>
            </Card>
          ))}
        </Stack>
      )}
    </>
  );
}
